import { IOperationBalanceItem } from '../../panel/models/panel.models';

/** Extrae ítems con `purchased > 0` del snapshot flat de GET /company/{id}/consumptions. */
export function contractedItemsFromSnapshot(
	snapshot: Record<string, unknown> | null | undefined,
): IOperationBalanceItem[] {
	if (!snapshot || typeof snapshot !== 'object') {
		return [];
	}

	const items: IOperationBalanceItem[] = [];
	for (const [key, value] of Object.entries(snapshot)) {
		if (!key.endsWith('_purchased')) {
			continue;
		}
		const billingKey = key.slice(0, -'_purchased'.length);
		if (!billingKey) {
			continue;
		}
		const purchased = toInt(value);
		if (purchased <= 0) {
			continue;
		}

		const remainingRaw = toInt(snapshot[`op_${billingKey}_remaining`]);
		const opKey = `op_${billingKey}`;
		const hasOpConsumed = Object.prototype.hasOwnProperty.call(snapshot, opKey);
		const consumedRaw = hasOpConsumed ? toInt(snapshot[opKey]) : Math.max(0, purchased - remainingRaw);

		items.push({
			billing_key: billingKey,
			purchased,
			remaining: remainingRaw,
			consumed: Math.max(0, consumedRaw),
		});
	}
	return items;
}

/**
 * Agrega por ítem (clamp por key) y luego suma.
 * Evita inflar/desinflar el total al sumar `remaining` negativos entre keys.
 */
export function sumOperationBalanceItems(items: IOperationBalanceItem[]): {
	purchased: number;
	remaining: number;
	consumed: number;
	percent: number;
} | null {
	if (!items?.length) {
		return null;
	}

	let purchased = 0;
	let remaining = 0;
	let consumed = 0;

	for (const item of items) {
		const p = Math.trunc(item.purchased);
		if (p <= 0) {
			continue;
		}
		const r = clampRemaining(p, item.remaining);
		const c = clampConsumed(p, item.consumed, r);
		purchased += p;
		remaining += r;
		consumed += c;
	}

	if (purchased <= 0) {
		return null;
	}

	return {
		purchased,
		remaining,
		consumed,
		percent: Math.round((consumed / purchased) * 100),
	};
}

/** % consumido acotado a purchased (barra / modal). */
export function itemBalancePercent(purchased: number, remaining: number, consumed?: number): number {
	const p = Math.trunc(purchased);
	if (p <= 0) {
		return 0;
	}
	const r = clampRemaining(p, remaining);
	const c = clampConsumed(p, consumed ?? p - r, r);
	return Math.round((c / p) * 100);
}

function clampRemaining(purchased: number, remaining: number): number {
	return Math.max(0, Math.min(Math.trunc(remaining), purchased));
}

/** Consumo mostrado en UI: no supera purchased (overage se refleja en remaining negativo del API). */
function clampConsumed(purchased: number, consumed: number, remainingClamped: number): number {
	const fromOp = Math.max(0, Math.trunc(consumed));
	if (Number.isFinite(fromOp) && fromOp > 0) {
		return Math.min(fromOp, purchased);
	}
	return Math.max(0, purchased - remainingClamped);
}

function toInt(value: unknown): number {
	if (value === null || value === undefined || value === '') {
		return 0;
	}
	const n = typeof value === 'number' ? value : Number(value);
	return Number.isFinite(n) ? Math.trunc(n) : 0;
}
